import { useEffect, useState } from "react";
import type { RefObject } from "react";

type AnyEvent = MouseEvent | TouchEvent;

interface IOptions {
    /** Если компонент находится в дереве всегда, нам нужно вызвать ререндер хука. Основываться будем не факте видимости компонента  */
    isOpen?: boolean;
}

const defaultOptions = {
    isOpen: true,
};

/**
 * Обработчик события клика по любой области экрана, кроме элемента.
 */
export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
    ref: RefObject<T>,
    handler: (event: AnyEvent) => void,
    options: IOptions = defaultOptions,
) => {
    const { isOpen } = {
        ...defaultOptions,
        ...options,
    };

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        /**
         * Устанавливаем слушатели только после маунта компонента, иначе была ситуация, что при клике
         * компонент успевал замаунтиться, обработчики вешались и успевали перехватить клик за счёт распространения
         * ивента
         */
        if (!isMounted) {
            return;
        }
        const listener = (event: AnyEvent) => {
            const el = ref?.current;

            if (!el || el.contains(event.target as Node)) {
                return;
            }

            handler(event);
        };

        document.addEventListener("click", listener, true);
        document.addEventListener("touchstart", listener, true);

        return () => {
            document.removeEventListener("click", listener, true);
            document.removeEventListener("touchstart", listener, true);
        };
    }, [ref, handler, isMounted]);

    useEffect(() => {
        setIsMounted(isOpen);
        return () => setIsMounted(false);
    }, [isOpen]);
};
